import React from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { InboxDown, FileCheck } from "@mynaui/icons-react";
import { useNavigate } from "react-router-dom";

// Wrapper for FileCheck Icon (Document with Checkmark)
const DocumentCheckIcon = (props) => {
  const { sx, ...otherProps } = props;
  const size = sx?.width || sx?.fontSize || 90;
  const color = sx?.color || "currentColor";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        ...sx,
      }}
    >
      <FileCheck
        width={size}
        height={size}
        color={color}
        strokeWidth={1}
        {...otherProps}
      />
    </Box>
  );
};

// Wrapper for InboxDown Icon
const InboxDownIcon = (props) => {
  const { sx, ...otherProps } = props;
  const size = sx?.width || sx?.fontSize || 90;
  const color = sx?.color || "currentColor";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        ...sx,
      }}
    >
      <InboxDown
        width={size}
        height={size}
        color={color}
        strokeWidth={1}
        {...otherProps}
      />
    </Box>
  );
};

const StatCard = ({
  count,
  title,
  subtitle,
  bgcolor,
  iconColor,
  icon: Icon,
  onClick,
}) => {
  const isAwaitingApproval = bgcolor === "#FFFBEB";
  const isWorkOrders = bgcolor === "#EFF6FF";

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        bgcolor,
        border: `1px solid ${
          isAwaitingApproval ? "#E17100" : isWorkOrders ? "#8EC5FF" : "#60A5FA"
        }`,
        borderRadius: "12px",
        boxShadow:
          "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.5, sm: 2.75, md: 3 },
          "&:last-child": { pb: { xs: 2.5, sm: 2.75, md: 3 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: 0,
            gap: { xs: 2, sm: 2.5, md: 3 },
            justifyContent: "space-between",
          }}
        >
          {/* Left side - Count and Text in horizontal layout */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            {/* Count */}
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "3rem", sm: "3.5rem", md: "4rem" },
                fontWeight: 700,
                lineHeight: 1,
                color: "#000",
              }}
            >
              {count}
            </Typography>

            {/* Title and Subtitle */}
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.0625rem" },
                  mb: "8px",
                  color: "#000",
                  lineHeight: 1.3,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: {
                    xs: "0.8125rem",
                    sm: "0.875rem",
                    md: "0.9375rem",
                  },
                  color: "#6B7280",
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>

          {/* Right side - Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "transparent",
              flex: "none",
              order: 1,
              flexGrow: 0,
            }}
          >
            <Icon
              sx={{
                fontSize: 80,
                color: iconColor,
                width: 80,
                height: 80,
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const QuickStats = ({ awaitingApproval, workOrders }) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M6 12L6 21" stroke="#020618" />
          <path d="M18 8V21" stroke="#020618" />
          <path d="M12 3V21" stroke="#020618" />
        </svg>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "18px",
            color: "#000",
            lineHeight: "28px",
          }}
        >
          Quick Stats
        </Typography>
      </Box>

      {/* Stats Cards - Flexbox Layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <StatCard
            count={awaitingApproval}
            title="Awaiting Approval"
            subtitle="Requests waiting for approval"
            bgcolor="#FFFBEB"
            iconColor="#E17100"
            icon={DocumentCheckIcon}
            onClick={() => navigate("/requests")}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatCard
            count={workOrders}
            title="Work Orders"
            subtitle="Number of Work Orders"
            bgcolor="#EFF6FF"
            iconColor="#155DFC"
            icon={InboxDownIcon}
            onClick={() => navigate("/work-orders")}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default QuickStats;
